/**
 * Fractional-octave smoothing on band data.
 *
 * Uses a sliding window in the log-frequency domain.
 * For 1/N-octave data with 1/N-octave smoothing, this is effectively
 * a 1-band moving average. For finer data smoothed to coarser resolution,
 * the window spans multiple bands.
 */

import type { OctaveBand } from "../types";

/**
 * Apply fractional-octave smoothing to band-level data.
 *
 * @param bandDb - dB values per band
 * @param bands - the band definitions (with center frequencies)
 * @param smoothingFraction - the octave fraction for smoothing width
 *   e.g. 3 means 1/3-octave smoothing window
 * @returns smoothed dB values
 */
export function smoothBands(
  bandDb: Float64Array,
  bands: OctaveBand[],
  smoothingFraction: number
): Float64Array {
  const result = new Float64Array(bandDb.length);
  const halfWidth = 1 / (2 * smoothingFraction); // in octaves

  for (let i = 0; i < bands.length; i++) {
    const centerLog = Math.log2(bands[i].center);
    let powerSum = 0;
    let count = 0;

    for (let j = 0; j < bands.length; j++) {
      const otherLog = Math.log2(bands[j].center);
      if (Math.abs(otherLog - centerLog) <= halfWidth) {
        powerSum += Math.pow(10, bandDb[j] / 10);
        count++;
      }
    }

    result[i] = count > 0 ? 10 * Math.log10(powerSum / count) : bandDb[i];
  }

  return result;
}
